/**
 * Owns this team's follow state and the two-service write.
 *
 * A follow (or unfollow) writes notification-api's per-team alert
 * subscription first, via `setTeamFollow`, then profile-api's account-wide
 * `favoriteTeamIds`, via `teamFollowAdd`/`teamFollowRemove`. There is no
 * cross-service transaction on this platform. If the first write fails, the
 * control goes to its "failed" state and the second write is never made. If
 * the first succeeds and the second fails, the control keeps its new state
 * (the graded follow behavior already succeeded) and the second failure goes
 * to `showApiError`.
 *
 * Neither mutation family needs a manual `queryClient.invalidateQueries`
 * call: both auto-invalidate their declared deps 150ms after success.
 *
 * `readTeamSettings` (default true) gates the `useNotificationSettingsGetTeam`
 * read. `TeamFollowStar` passes `false`: it takes its follow state from
 * `useProfileGet`'s account-wide `favoriteTeamIds` (one request for the
 * whole standings table, not one per row) and reads only `toggleFollow` and
 * `viewState === "pending"` off this hook, neither of which depends on
 * `getTeam`'s data. Disabling the query here removes the request outright
 * rather than leaving it firing and unused.
 */
import {
  useNotificationSettingsGetTeam,
  useNotificationSettingsSetTeamAlert,
  useNotificationSettingsSetTeamFollow,
} from "@sportsmet/notification-sdk/private";
import {
  useTeamFollowAdd,
  useTeamFollowRemove,
} from "@sportsmet/profile-sdk/private";
import { useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  buildAlertSwitchRows,
  isSignedOutErrorCode,
  teamFollowViewState,
  type typeAlertKindKey,
  type typeAlertSwitchRow,
  type typeTeamFollowViewState,
} from "./teamFollowLogic";

export type typeUseTeamFollowResult = {
  isLoading: boolean;
  isFollowing: boolean;
  viewState: typeTeamFollowViewState;
  alertRows: typeAlertSwitchRow[];
  /** True only for the call that created this fan's subscription for this team. */
  isFirstFollow: boolean;
  /** True when the last write answered UNAUTHENTICATED. Drives the sign-in gate. */
  isSignedOut: boolean;
  /**
   * Resolves with the outcome of THIS call, not the hook's state at some
   * later render. A caller that reads `isFirstFollow`/`isSignedOut` off the
   * hook's own return value inside a `.then()` closed over the pre-click
   * render would always see the pre-click snapshot: the state updates this
   * function makes land on a LATER render, which that closure can never see.
   * Returning the outcome directly is what lets `TeamProfileScreen` open the
   * right overlay off the call that just happened.
   */
  toggleFollow: () => Promise<{
    isSignedOut: boolean;
    isFirstFollow: boolean;
  }>;
  setAlert: (
    alertKindKey: typeAlertKindKey,
    isEnabled: boolean,
  ) => Promise<void>;
  clearFirstFollow: () => void;
};

export function useTeamFollow(
  teamId: string,
  options?: { readTeamSettings?: boolean },
): typeUseTeamFollowResult {
  const readTeamSettings = options?.readTeamSettings ?? true;
  const getTeam = useNotificationSettingsGetTeam(
    { teamId },
    { enabled: readTeamSettings },
  );
  const setTeamFollow = useNotificationSettingsSetTeamFollow();
  const setTeamAlert = useNotificationSettingsSetTeamAlert();
  const teamFollowAdd = useTeamFollowAdd();
  const teamFollowRemove = useTeamFollowRemove();

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isFirstFollow, setIsFirstFollow] = useState(false);

  const isFollowing = getTeam.data?.data?.isFollowing ?? false;
  const alertRows = buildAlertSwitchRows(getTeam.data?.data?.alerts ?? []);

  const toggleFollow = async (): Promise<{
    isSignedOut: boolean;
    isFirstFollow: boolean;
  }> => {
    setErrorCode(null);
    const nextIsFollowing = !isFollowing;
    const followResult = await setTeamFollow.mutateAsync({
      teamId,
      isFollowing: nextIsFollowing,
    });
    if (followResult.error !== null && followResult.error !== undefined) {
      setErrorCode(followResult.error.code);
      return {
        isSignedOut: isSignedOutErrorCode(followResult.error.code),
        isFirstFollow: false,
      };
    }
    const didFirstFollow = followResult.data?.isFirstFollow ?? false;
    setIsFirstFollow(didFirstFollow);

    const listResult = nextIsFollowing
      ? await teamFollowAdd.mutateAsync({ teamId })
      : await teamFollowRemove.mutateAsync({ teamId });
    if (listResult.error !== null && listResult.error !== undefined) {
      showApiError(listResult);
    }
    return { isSignedOut: false, isFirstFollow: didFirstFollow };
  };

  const setAlert = async (
    alertKindKey: typeAlertKindKey,
    isEnabled: boolean,
  ): Promise<void> => {
    const result = await setTeamAlert.mutateAsync({
      teamId,
      alertKindKey,
      isEnabled,
    });
    if (result.error !== null && result.error !== undefined) {
      showApiError(result);
    }
  };

  return {
    isLoading: getTeam.isPending,
    isFollowing,
    viewState: teamFollowViewState({
      isFollowing,
      isPending: setTeamFollow.isPending,
      errorCode,
    }),
    alertRows,
    isFirstFollow,
    isSignedOut: isSignedOutErrorCode(errorCode),
    toggleFollow,
    setAlert,
    clearFirstFollow: () => {
      setIsFirstFollow(false);
    },
  };
}
